import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Home, LineChart as LineIcon, MessageSquare, User, LogOut, Globe, Play, Check,
  AlertTriangle, Download, ChevronRight, Send } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiError, API } from "../../api";
import { useAuth } from "../../auth";
import { useI18n, LANGS } from "../../i18n";
import { LOGO, Button, Card, Input, Textarea, Field, Modal, Badge, Spinner, EmptyState, Slider, cx } from "../../components/ui";
import { toast } from "sonner";

export default function Portal() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [view, setView] = useState("home");

  const nav = [["home", Home, t("today_program")], ["progress", LineIcon, t("my_progress")],
    ["messages", MessageSquare, t("messages")], ["profile", User, t("profile")]];

  return (
    <div className="min-h-screen bg-vlg pb-20">
      <header className="sticky top-0 z-30 bg-navy text-white h-14 flex items-center px-4 gap-3">
        <img src={LOGO} className="h-8 w-8 object-contain bg-white rounded-lg p-0.5" alt="SpinaGo" />
        <span className="font-head font-bold">Spina<span className="text-teal-400">Go</span></span>
        <div className="ms-auto flex items-center gap-2">
          <select value={lang} onChange={(e) => setLang(e.target.value)} data-testid="portal-lang" className="bg-white/10 text-white text-sm rounded-lg px-2 py-1 border-0">
            {LANGS.map((l) => <option key={l.code} value={l.code} className="text-navy">{l.label}</option>)}
          </select>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="portal-logout" className="p-1.5 hover:bg-white/10 rounded-lg"><LogOut size={18} /></button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4">
        {view === "home" && <PortalHome t={t} />}
        {view === "progress" && <PortalProgress t={t} />}
        {view === "messages" && <PortalMessages t={t} user={user} />}
        {view === "profile" && <PortalProfile t={t} user={user} />}
      </main>

      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-30">
        <div className="max-w-2xl mx-auto grid grid-cols-4">
          {nav.map(([k, Icon, label]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`portal-nav-${k}`}
              className={cx("flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-medium", view === k ? "text-teal" : "text-slate-400")}>
              <Icon size={22} /> {label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
}

function PortalHome({ t }) {
  const [data, setData] = useState(null);
  const [exMap, setExMap] = useState({});
  const [active, setActive] = useState(null);

  const load = () => api.get("/portal/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const prog = data?.active_program;
    if (!prog) return;
    (async () => {
      const map = {};
      await Promise.all((prog.items || []).map(async (it) => {
        try { const r = await api.get(`/portal/exercise/${it.exercise_id}`); map[it.exercise_id] = r.data; } catch (err) { console.error("portal exercise load failed", it.exercise_id, err); }
      }));
      setExMap(map);
    })();
  }, [data?.active_program?.id]);

  if (!data) return <div className="flex justify-center py-16"><Spinner /></div>;
  const prog = data.active_program;
  const doneToday = new Set(data.done_today || []);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-head font-bold text-navy text-2xl">{t("welcome")}, {data.patient?.first_name} 👋</h1>
        <p className="text-slate-500 text-sm">{t("today_program")}</p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3 text-center"><div className="text-xl font-bold font-head text-teal">{(prog?.items || []).length}</div><div className="text-[11px] text-slate-400">{t("to_do")}</div></Card>
        <Card className="p-3 text-center"><div className="text-xl font-bold font-head text-navy">{doneToday.size}</div><div className="text-[11px] text-slate-400">{t("done")}</div></Card>
        <Card className="p-3 text-center"><div className="text-xs font-bold font-head text-warn mt-1">{data.reeval_date || "—"}</div><div className="text-[11px] text-slate-400">{t("next_reeval")}</div></Card>
      </div>

      {!prog ? <EmptyState title={t("no_program")} /> : (
        <div className="space-y-3">
          {prog.goal && <Card className="p-4 bg-teal-50 border-teal-100"><div className="text-xs text-teal-600 font-semibold uppercase">{t("program_goal")}</div><div className="text-navy font-medium">{prog.goal}</div></Card>}
          {(prog.items || []).map((it, i) => {
            const ex = exMap[it.exercise_id];
            const done = doneToday.has(it.exercise_id);
            return (
              <Card key={`${it.exercise_id}-${i}`} className={cx("p-3 flex items-center gap-3 cursor-pointer hover:shadow-card transition", done && "opacity-70")} onClick={() => setActive({ item: it, ex, program: prog })} data-testid={`portal-ex-${it.exercise_id}`}>
                <div className="h-14 w-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">
                  {ex?.image_url && <img src={ex.image_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-navy truncate">{i + 1}. {ex?.name_fr || "Exercice"}</div>
                  <div className="text-xs text-slate-400">{it.sets} × {it.reps} · {it.frequency}</div>
                </div>
                {done ? <Badge tone="green"><Check size={12} /> {t("done")}</Badge> : <ChevronRight className="text-slate-300 rtl:rotate-180" />}
              </Card>
            );
          })}
          <Button variant="outline" className="w-full" onClick={() => downloadPdf(prog.id, "fr")} data-testid="portal-pdf"><Download size={16} /> {t("download_pdf")}</Button>
        </div>
      )}

      <ExerciseScreen active={active} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} t={t} />
    </div>
  );
}

async function downloadPdf(gid, lang) {
  try {
    const res = await api.get(`/programs/${gid}/pdf?lang=${lang}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a");
    a.href = url; a.download = `mon-programme.pdf`; a.click(); URL.revokeObjectURL(url);
  } catch { toast.error("Erreur PDF"); }
}

function ExerciseScreen({ active, onClose, onDone, t }) {
  const [started, setStarted] = useState(false);
  const [f, setF] = useState({ pain_before: 0, pain_after: 0, difficulty: 0, reps_done: 0, comment: "" });
  useEffect(() => { setStarted(false); setF({ pain_before: 0, pain_after: 0, difficulty: 0, reps_done: active?.item?.reps || 0, comment: "" }); }, [active]);
  if (!active) return null;
  const { ex, item, program } = active;

  const finish = async () => {
    try {
      await api.post("/completions", { program_id: program.id, exercise_id: item.exercise_id, completed: true,
        sets_done: item.sets, reps_done: f.reps_done, duration_done: item.duration,
        pain_before: f.pain_before, pain_after: f.pain_after, difficulty: f.difficulty, comment: f.comment });
      toast.success("Exercice enregistré 💪");
      onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Modal open={!!active} onClose={onClose} title={ex?.name_fr || "Exercice"} size="md">
      <div className="space-y-4">
        {ex?.video_url ? <video src={ex.video_url} controls className="w-full rounded-lg bg-black aspect-video" /> :
          ex?.image_url ? <img src={ex.image_url} alt={ex.alt_text} className="w-full rounded-lg aspect-video object-cover" /> : null}

        <div className="grid grid-cols-4 gap-2 text-center">
          {[["sets", item.sets], ["reps", item.reps], ["duration", item.duration], ["rest", item.rest]].map(([k, v]) => (
            <div key={k} className="bg-vlg rounded-lg py-2"><div className="font-bold font-head text-navy text-sm">{v || "—"}</div><div className="text-[10px] text-slate-400">{t(k)}</div></div>
          ))}
        </div>

        {ex?.start_position && <Info label={t("start_position")} value={ex.start_position} />}
        {ex?.instructions && <Info label={t("instructions")} value={ex.instructions} />}
        {ex?.mistakes && <Info label={t("mistakes")} value={ex.mistakes} tone="warn" />}
        {ex?.precautions && <Info label={t("precautions")} value={ex.precautions} tone="warn" />}

        {!started ? (
          <Button className="w-full" size="lg" onClick={() => setStarted(true)} data-testid="ex-start"><Play size={18} /> {t("start")}</Button>
        ) : (
          <div className="space-y-4 border-t border-slate-100 pt-4">
            <div><Field label={`${t("pain_before")} (0-10)`}><Slider value={f.pain_before} onChange={(v) => setF({ ...f, pain_before: v })} tone="alert" data-testid="ex-pain-before" /></Field></div>
            <div><Field label={`${t("pain_after")} (0-10)`}><Slider value={f.pain_after} onChange={(v) => setF({ ...f, pain_after: v })} tone="alert" data-testid="ex-pain-after" /></Field></div>
            <div><Field label={`${t("difficulty_level")} (0-10)`}><Slider value={f.difficulty} onChange={(v) => setF({ ...f, difficulty: v })} data-testid="ex-difficulty" /></Field></div>
            <Field label={t("reps_done")}><Input type="number" value={f.reps_done} onChange={(e) => setF({ ...f, reps_done: Number(e.target.value) })} data-testid="ex-reps-done" /></Field>
            <Field label={t("comment")}><Textarea rows={2} value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="ex-comment" /></Field>
            <Button className="w-full" size="lg" onClick={finish} data-testid="ex-finish"><Check size={18} /> {t("finish")}</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Info({ label, value, tone }) {
  return <div className={cx("rounded-lg px-3 py-2 text-sm", tone === "warn" ? "bg-orange-50 text-slate-700" : "bg-vlg text-slate-600")}>
    <span className="text-xs font-semibold text-slate-400 uppercase block mb-0.5">{label}</span>{value}</div>;
}

function PortalProgress({ t }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/portal/progress").then((r) => setData(r.data)).catch(() => setData({ series: [], total_completed: 0 })); }, []);
  if (!data) return <div className="flex justify-center py-16"><Spinner /></div>;
  return (
    <div className="space-y-4">
      <h1 className="font-head font-bold text-navy text-2xl">{t("my_progress")}</h1>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4 text-center"><div className="text-3xl font-bold font-head text-teal">{data.total_completed}</div><div className="text-xs text-slate-400">Exercices réalisés</div></Card>
        <Card className="p-4 text-center"><div className="text-3xl font-bold font-head text-navy">{data.series.length}</div><div className="text-xs text-slate-400">Jours actifs</div></Card>
      </div>
      <Card className="p-4">
        <h3 className="font-head font-semibold text-navy mb-3 text-sm">Douleur moyenne / jour</h3>
        {data.series.length === 0 ? <EmptyState title={t("no_data")} /> : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tickFormatter={(d) => d?.slice(5)} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <Tooltip />
              <Line type="monotone" dataKey="avg_pain" stroke="#dc2626" strokeWidth={2} dot={{ r: 3 }} name="Douleur" />
              <Line type="monotone" dataKey="completed" stroke="#3aa88f" strokeWidth={2} dot={{ r: 3 }} name="Réalisés" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

function PortalMessages({ t, user }) {
  const [msgs, setMsgs] = useState([]);
  const [conv, setConv] = useState(null);
  const [body, setBody] = useState("");
  const [pros, setPros] = useState([]);
  const endRef = useRef(null);

  const load = async () => {
    const r = await api.get("/conversations").catch(() => ({ data: [] }));
    if (r.data.length) { setConv(r.data[0]); const m = await api.get(`/conversations/${r.data[0].id}/messages`).catch(() => ({ data: [] })); setMsgs(m.data); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!body.trim()) return;
    try {
      let convId = conv?.id;
      if (!convId) {
        const { data: recips } = await api.get("/messages/recipients");
        if (!recips.length) { toast.info("Aucun professionnel disponible pour le moment."); return; }
        await api.post("/messages", { recipient_id: recips[0].id, body });
      } else {
        await api.post("/messages", { conversation_id: convId, body });
      }
      setBody("");
      const r = await api.get("/conversations");
      if (r.data.length) { setConv(r.data[0]); const m = await api.get(`/conversations/${r.data[0].id}/messages`); setMsgs(m.data); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <h1 className="font-head font-bold text-navy text-2xl">{t("messages")}</h1>
      <div className="bg-orange-50 border border-orange-100 text-warn text-xs rounded-lg px-3 py-2 flex items-center gap-2"><AlertTriangle size={14} /> {t("emergency_notice")}</div>
      <Card className="flex flex-col h-[60vh]">
        <div className="flex-1 overflow-y-auto spina-scroll p-3 space-y-2">
          {!conv ? <EmptyState title="Aucune conversation" hint="Écrivez ci-dessous pour contacter votre professionnel." /> :
            msgs.map((m) => (
              <div key={m.id} className={cx("flex", m.sender_id === user.id ? "justify-end" : "justify-start")}>
                <div className={cx("max-w-[80%] rounded-2xl px-3.5 py-2 text-sm", m.sender_id === user.id ? "bg-teal text-white" : "bg-slate-100 text-slate-700")}>{m.body}</div>
              </div>
            ))}
          <div ref={endRef} />
        </div>
        <div className="border-t border-slate-200 p-3 flex gap-2">
          <Input value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("message_placeholder")} data-testid="portal-msg-input" onKeyDown={(e) => e.key === "Enter" && send()} />
          <Button onClick={send} data-testid="portal-msg-send"><Send size={16} /></Button>
        </div>
      </Card>
    </div>
  );
}

function PortalProfile({ t, user }) {
  const { refresh } = useAuth();
  const { lang, setLang } = useI18n();
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [prof, setProf] = useState({ phone: user.phone || "", address: user.address || "" });

  const saveProfile = async () => {
    try { await api.put("/portal/profile", { ...prof, lang }); toast.success("Profil mis à jour"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const changePw = async () => {
    if (pw.new_password.length < 8) return toast.error("8 caractères minimum");
    try { await api.post("/auth/change-password", pw); toast.success("Mot de passe modifié"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-head font-bold text-navy text-2xl">{t("profile")}</h1>
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-teal text-white flex items-center justify-center font-head font-bold">{user.name?.[0]}</div>
          <div><div className="font-medium text-navy">{user.name}</div><div className="text-xs text-slate-400">{user.email}</div></div>
        </div>
        <Field label={t("phone")}><Input value={prof.phone} onChange={(e) => setProf({ ...prof, phone: e.target.value })} /></Field>
        <Field label={t("address")}><Input value={prof.address} onChange={(e) => setProf({ ...prof, address: e.target.value })} /></Field>
        <Field label={t("lang")}>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm">
            {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </Field>
        <Button onClick={saveProfile} data-testid="profile-save">{t("save")}</Button>
      </Card>
      <Card className="p-5 space-y-3">
        <h3 className="font-head font-semibold text-navy text-sm">{t("change_password")}</h3>
        <Field label="Mot de passe actuel"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></Field>
        <Field label="Nouveau mot de passe"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></Field>
        <Button variant="outline" onClick={changePw} data-testid="change-pw">{t("change_password")}</Button>
      </Card>
    </div>
  );
}
